import { formatDesktopNotification } from '../utils/notificationMessageUtils';
import { isMessageMentioningUser } from '../utils/mentionUtils';
import { formatReactionToMessageLine } from '../utils/reactionConfig';
import { isNotificationSpeechEnabled } from '../utils/workModePrefs';
import { isElectronRuntime } from '../realm/isRealmEnabled';

class NotificationService {
  constructor() {
    this.permission = null;
    this.isTabVisible = true;
    this.isWindowFocused = true; // Track if browser window has focus
    this.isMessageInputFocused = false; // Track if message input is focused
    this.audio = null;
    this.audioContext = null;
    this.audioUnlocked = false; // Track if audio has been unlocked via user interaction
    this.speechUnlocked = false;
    this.lastSpeechKey = '';
    this.lastSpeechAt = 0;
    this.lastSoundAt = 0;
    this.recentMessageAlerts = new Map();
    this.init();
  }

  init() {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.warn('This browser does not support desktop notifications');
      return;
    }

    // Check current permission
    this.permission = Notification.permission;

    // Request permission if not already granted or denied
    if (this.permission === 'default') {
      this.requestPermission();
    }

    // Track tab visibility
    document.addEventListener('visibilitychange', () => {
      this.isTabVisible = !document.hidden;
    });

    // Track window focus/blur (when another application is on top)
    window.addEventListener('focus', () => {
      this.isWindowFocused = true;
    });

    window.addEventListener('blur', () => {
      this.isWindowFocused = false;
    });

    // Initialize window focus state
    this.isWindowFocused = document.hasFocus ? document.hasFocus() : true;

    // Initialize audio for notifications
    this.initAudio();

    // Unlock audio on first user interaction (required for mobile browsers)
    this.unlockAudioOnInteraction();
  }

  initAudio() {
    // Try to load audio file from public folder
    // If the file doesn't exist, we'll use Web Audio API as fallback
    this.audio = new Audio();
    this.audio.volume = 0.5; // Set volume to 50%
    this.audio.preload = 'auto';

    // Try to load notification sound from public folder
    // You can add your own audio file at /public/sounds/notification.mp3
    const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    this.audio.src = `${publicBase}/sounds/notification.mp3`;

    // Handle error if file doesn't exist - we'll use fallback
    this.audio.onerror = () => {
      console.log('Audio file not found, will use fallback sound');
      // Mark audio as unavailable
      this.audio = null;
    };

    // Preload the audio file
    this.audio.load();

    // Initialize AudioContext for fallback sound (will be resumed on user interaction)
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  }

  unlockAudioOnInteraction() {
    // Mobile browsers require user interaction to unlock audio
    // This function sets up listeners to unlock audio on first interaction
    const unlockAudio = async () => {
      if (this.audioUnlocked) return;

      // Unlock HTML5 Audio by playing and immediately pausing
      if (this.audio) {
        try {
          // Set volume and prepare audio
          this.audio.volume = 0.5;
          this.audio.currentTime = 0;

          const playPromise = this.audio.play();
          if (playPromise !== undefined) {
            await playPromise;
            this.audio.pause();
            this.audio.currentTime = 0;
            this.audioUnlocked = true;
            console.log('✅ HTML5 Audio unlocked via user interaction');
          }
        } catch (error) {
          console.warn('Could not unlock HTML5 Audio:', error);
        }
      }

      // Resume AudioContext if suspended (required for mobile)
      if (this.audioContext) {
        if (this.audioContext.state === 'suspended') {
          try {
            await this.audioContext.resume();
            console.log('✅ AudioContext resumed via user interaction');
            this.audioUnlocked = true;
          } catch (error) {
            console.warn('Could not resume AudioContext:', error);
          }
        } else if (this.audioContext.state === 'running') {
          this.audioUnlocked = true;
        }
      }
    };

    // Listen for various user interaction events
    // Use 'once: true' to only unlock on first interaction
    const events = ['touchstart', 'touchend', 'mousedown', 'keydown', 'click'];
    const unlockOnce = () => {
      if (!this.audioUnlocked) {
        unlockAudio();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, unlockOnce, { once: true, passive: true });
    });
  }

  async playNotificationSound() {
    const now = Date.now();
    if (now - this.lastSoundAt < 1200) return;
    this.lastSoundAt = now;
    // Play sound regardless of tab visibility
    // The caller should determine if sound should play based on context

    // If audio is not unlocked yet, try to unlock it first (for mobile)
    if (!this.audioUnlocked) {
      // Try to unlock audio - this will work if called from user interaction context
      await this.tryUnlockAudio();
    }

    try {
      // Try to use HTML5 Audio if available
      if (this.audio) {
        // Reset audio to beginning and play
        this.audio.currentTime = 0;
        const playPromise = this.audio.play();

        if (playPromise !== undefined) {
          playPromise.catch(error => {
            // If play fails, use fallback sound
            console.warn('Could not play notification sound, using fallback:', error);
            this.playFallbackSound();
          });
        }
      } else {
        // Audio file not available, use fallback sound
        this.playFallbackSound();
      }
    } catch (error) {
      console.warn('Error playing notification sound, using fallback:', error);
      // Fallback to Web Audio API
      this.playFallbackSound();
    }
  }

  async tryUnlockAudio() {
    // Try to unlock audio immediately (works if called from user interaction)
    if (this.audioUnlocked) return;

    // Unlock HTML5 Audio
    if (this.audio) {
      try {
        this.audio.volume = 0.5;
        this.audio.currentTime = 0;
        const playPromise = this.audio.play();
        if (playPromise !== undefined) {
          await playPromise;
          this.audio.pause();
          this.audio.currentTime = 0;
          this.audioUnlocked = true;
          console.log('✅ HTML5 Audio unlocked');
        }
      } catch (error) {
        // Silent fail - will try fallback
      }
    }

    // Resume AudioContext
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        this.audioUnlocked = true;
        console.log('✅ AudioContext resumed');
      } catch (error) {
        // Silent fail
      }
    }
  }

  async playFallbackSound() {
    try {
      // Use existing AudioContext or create new one
      let audioContext = this.audioContext;

      if (!audioContext) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.audioContext = audioContext;
      }

      // Resume AudioContext if suspended (required for mobile browsers)
      if (audioContext.state === 'suspended') {
        try {
          await audioContext.resume();
          console.log('✅ AudioContext resumed for fallback sound');
        } catch (resumeError) {
          console.warn('Could not resume AudioContext:', resumeError);
          // Try to create a new context as fallback
          audioContext = new (window.AudioContext || window.webkitAudioContext)();
          this.audioContext = audioContext;
        }
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configure sound: a pleasant notification beep
      oscillator.frequency.value = 800; // Frequency in Hz
      oscillator.type = 'sine'; // Sine wave for a smooth sound

      // Set volume
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      // Play the sound
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3); // 300ms duration
    } catch (error) {
      console.warn('Could not play fallback notification sound:', error);
    }
  }

  async requestPermission() {
    if (!('Notification' in window)) {
      return false;
    }

    if (this.permission === 'granted') {
      return true;
    }

    if (this.permission === 'denied') {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  showNotification(title, options = {}) {
    const { speak, spoken, ...notificationOptions } = options;
    if (speak !== false) {
      this.speakNotification(spoken || title, spoken ? '' : notificationOptions.body);
    }

    const electronShell = isElectronRuntime();
    // Electron stays "visible" while unfocused; still show an OS banner.
    if (electronShell) {
      if (this.isWindowFocused && this.isTabVisible) {
        return null;
      }
      try {
        window.workloopRealm?.showNotification?.({
          title,
          body: notificationOptions.body || '',
          tag: notificationOptions.tag || 'workloop-message',
          data: notificationOptions.data || null,
        });
      } catch (error) {
        console.error('Error showing Electron notification:', error);
      }
      return null;
    }

    // Don't show notification if tab is visible
    if (this.isTabVisible) {
      return null;
    }

    // Don't show notification if permission is not granted
    if (this.permission !== 'granted') {
      return null;
    }

    try {
      const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
      const notification = new Notification(title, {
        icon: `${publicBase}/images/favicon.png`,
        badge: `${publicBase}/images/favicon.png`,
        tag: notificationOptions.tag || 'workloop-message', // Group notifications
        requireInteraction: false,
        silent: false,
        ...notificationOptions
      });

      // Focus window when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  // Get notification sound preference for a specific channel
  // Returns true if sound is enabled (default), false if disabled
  getChannelSoundPreference(channelId) {
    if (!channelId) return true; // Default to enabled if no channelId

    try {
      const preferences = this.loadSoundPreferences();
      // If channel preference exists, return it; otherwise default to true (enabled)
      return preferences[channelId] !== undefined ? preferences[channelId] : true;
    } catch (error) {
      console.warn('Error loading sound preferences:', error);
      return true; // Default to enabled on error
    }
  }

  // Set notification sound preference for a specific channel
  setChannelSoundPreference(channelId, enabled) {
    if (!channelId) return;

    try {
      const preferences = this.loadSoundPreferences();
      preferences[channelId] = enabled;
      this.saveSoundPreferences(preferences);
    } catch (error) {
      console.error('Error saving sound preferences:', error);
    }
  }

  // Load all sound preferences from localStorage
  loadSoundPreferences() {
    try {
      const stored = localStorage.getItem('notificationSoundPreferences');
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('Error parsing sound preferences from localStorage:', error);
      return {};
    }
  }

  // Save all sound preferences to localStorage
  saveSoundPreferences(preferences) {
    try {
      localStorage.setItem('notificationSoundPreferences', JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving sound preferences to localStorage:', error);
    }
  }

  // Get all sound preferences (for debugging or UI display)
  getAllSoundPreferences() {
    return this.loadSoundPreferences();
  }

  notifyInAppMention(message, channelName, isDirectMessage, currentChannelId = null, currentUser = null) {
    if (!message || !currentUser?.id) {
      return;
    }

    const viewingThisChat = currentChannelId != null
      && String(currentChannelId) === String(message.channelId);
    if (viewingThisChat && this.isTabVisible && this.isWindowFocused) {
      return;
    }

    const isDifferentChannel =
      currentChannelId != null && String(message.channelId) !== String(currentChannelId);
    const shouldPlaySound =
      isDifferentChannel ||
      !this.isTabVisible ||
      !this.isWindowFocused ||
      !this.isMessageInputFocused;

    if (shouldPlaySound) {
      this.playNotificationSound();
    }

    const { title, body, spoken } = formatDesktopNotification({
      message,
      channelName,
      isDirectMessage,
      isMentioned: true,
    });

    this.showNotification(title, {
      body,
      spoken,
      tag: `mention-${message.channelId}-${message.id}`,
      data: {
        channelId: message.channelId,
        messageId: message.id,
        replyToMessageId: message.replyToMessageId || null,
        isMention: true,
      },
    });
  }

  showMessageNotification(message, channelName, isDirectMessage, currentChannelId = null, currentUser = null) {
    if (!message || !message.senderName) {
      return;
    }
    const alertId = message.id || `${message.channelId}-${message.timestamp || message.createdAt || ''}-${message.content || ''}`;
    const now = Date.now();
    const lastAlert = this.recentMessageAlerts.get(alertId);
    if (lastAlert && now - lastAlert < 15000) {
      return;
    }
    this.recentMessageAlerts.set(alertId, now);

    const viewingThisChat = currentChannelId != null
      && String(currentChannelId) === String(message.channelId);
    if (viewingThisChat && this.isTabVisible && this.isWindowFocused) {
      return;
    }

    // Check if sound is enabled for this channel
    const soundEnabled = this.getChannelSoundPreference(message.channelId);

    // Play audio notification sound if:
    // 1. Sound is enabled for this channel, AND
    // 2. (Tab is not visible, OR
    //     Browser window is not focused (another app is on top), OR
    //     Message input is not focused (cursor not in text box), OR
    //     Message is from a different channel than the one currently being viewed)
    const isDifferentChannel = currentChannelId !== null && String(message.channelId) !== String(currentChannelId);
    const shouldPlaySound = soundEnabled && (
      !this.isTabVisible ||
      !this.isWindowFocused ||
      !this.isMessageInputFocused ||
      isDifferentChannel
    );

    const isMentioned = currentUser?.id && (
      (Array.isArray(message.mentionedUserIds) && message.mentionedUserIds.includes(currentUser.id)) ||
      isMessageMentioningUser(message, currentUser.id, currentUser.username)
    );
    // Speech is only for 1:1 chats and @mentions — not ordinary channel messages.
    const shouldSpeak = Boolean(isDirectMessage) || Boolean(isMentioned);

    if (shouldPlaySound && !(shouldSpeak && isNotificationSpeechEnabled())) {
      this.playNotificationSound();
    }

    const { title, body, spoken } = formatDesktopNotification({
      message,
      channelName,
      isDirectMessage,
      isMentioned,
    });

    this.showNotification(title, {
      body,
      spoken,
      speak: shouldSpeak,
      tag: `message-${message.channelId}`,
      data: {
        channelId: message.channelId,
        messageId: message.id,
        replyToMessageId: message.replyToMessageId || null,
      }
    });
  }

  showReactionNotification({
    reactorName,
    reactionLabel,
    reactionId,
    messagePreview,
    channelName,
    isDirectMessage,
    channelId,
    messageId,
    currentChannelId = null,
  }) {
    if (!channelId || !messageId) return;

    const soundEnabled = this.getChannelSoundPreference(channelId);
    const viewingThisChat = currentChannelId != null
      && String(currentChannelId) === String(channelId);
    if (viewingThisChat && this.isTabVisible && this.isWindowFocused) {
      return;
    }

    const isDifferentChannel =
      currentChannelId !== null && String(channelId) !== String(currentChannelId);
    const shouldPlaySound =
      soundEnabled &&
      (!this.isTabVisible ||
        !this.isWindowFocused ||
        !this.isMessageInputFocused ||
        isDifferentChannel);

    const shouldSpeak = Boolean(isDirectMessage);
    if (shouldPlaySound && !(shouldSpeak && isNotificationSpeechEnabled())) {
      this.playNotificationSound();
    }

    const title = isDirectMessage
      ? channelName || 'Direct Message'
      : channelName
        ? (channelName.startsWith('#') ? channelName : `#${channelName}`)
        : '#Channel';
    const body = formatReactionToMessageLine(
      reactionId || reactionLabel,
      messagePreview
    );

    this.showNotification(title, {
      body,
      speak: shouldSpeak,
      tag: `reaction-${channelId}-${messageId}`,
      data: {
        type: 'MESSAGE_REACTION',
        channelId,
        messageId,
      },
    });
  }

  primeSpeech() {
    this.speechUnlocked = true;
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      const synth = window.speechSynthesis;
      synth.getVoices();
      if (typeof synth.resume === 'function') synth.resume();
    } catch {
      // ignore
    }
  }

  speakNotification(title, body, options = {}) {
    let inCall = false;
    try {
      inCall = String(JSON.parse(localStorage.getItem('workloop_user') || 'null')?.status || '').toUpperCase() === 'IN_A_CALL';
    } catch {
      inCall = false;
    }
    if (inCall) {
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
      return;
    }
    const titleText = String(title || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/\s+/g, ' ')
      .trim();
    const bodyText = String(body || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/\s+/g, ' ')
      .trim();
    let text = titleText;
    if (bodyText) {
      const titleNorm = titleText.toLowerCase();
      const bodyNorm = bodyText.toLowerCase();
      if (!titleText || bodyNorm === titleNorm || bodyNorm.startsWith(titleNorm)) {
        text = bodyText;
      } else if (titleNorm.includes(bodyNorm)) {
        text = titleText;
      } else {
        text = `${titleText}. ${bodyText}`;
      }
    }
    if (!text) return;
    const now = Date.now();
    const speechKey = text.toLowerCase();
    if (!options.fromUserGesture && this.lastSpeechKey === speechKey && now - this.lastSpeechAt < 4000) {
      return;
    }
    this.lastSpeechKey = speechKey;
    this.lastSpeechAt = now;

    if (!isNotificationSpeechEnabled()) {
      return;
    }

    const nativeSpeak = window.workloopRealm?.speakText;
    if (typeof nativeSpeak === 'function') {
      nativeSpeak(text).then((result) => {
        if (result && result.ok === false) {
          this.speakWithWebSpeech(text, options);
        }
      }).catch(() => {
        this.speakWithWebSpeech(text, options);
      });
      return;
    }

    this.speakWithWebSpeech(text, options);
  }

  speakWithWebSpeech(text, options = {}) {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const synth = window.speechSynthesis;
    try {
      if (typeof synth.resume === 'function') synth.resume();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.volume = 1;
      utterance.rate = 1;
      utterance.pitch = 1;
      this.speechUnlocked = true;
      synth.speak(utterance);
      if (!options.fromUserGesture) {
        window.setTimeout(() => {
          if (synth.paused) synth.resume();
        }, 250);
      }
    } catch (error) {
      console.warn('Could not speak notification:', error);
    }
  }

  isTabVisibleNow() {
    return this.isTabVisible;
  }

  isWindowFocusedNow() {
    return this.isWindowFocused;
  }

  setMessageInputFocused(focused) {
    this.isMessageInputFocused = focused;
  }

  isMessageInputFocusedNow() {
    return this.isMessageInputFocused;
  }

  hasPermission() {
    return this.permission === 'granted';
  }

  isSpeechEnabled() {
    return isNotificationSpeechEnabled();
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;

